import { Chip } from "@mui/material";
import { GridFilterOperator, GridRenderCellParams, getGridStringOperators } from "@mui/x-data-grid-pro";
import { dateColDef } from "components/DataGrid/columnDefs/dateColDef";
import { editableColDefWithValidation } from "components/DataGrid/columnDefs/editableColDefWithValidation";
import { linkColDef } from "components/DataGrid/columnDefs/linkColDef";
import { singleSelectColDef } from "components/DataGrid/columnDefs/singleSelectColDef";
import { warehouseColDef } from "components/DataGrid/columnDefs/warehouseColDef";
import { yearColDef } from "components/DataGrid/columnDefs/yearColDef";
import { notContainsStringOperator } from "components/DataGrid/operators/notContainsStringOperator";
import { GridCellExpand } from "components/GridCellExpand";
import { colors } from "constants/colors";
import { conditions } from "constants/conditions";
import { deviceTypes } from "constants/device_types";
import { enrolledStatuses } from "constants/enrolled_statuses";
import { IndexedInventory } from "types/inventory";
import { inventoryEditValidation } from "validations/inventory";

function renderCommentsCell(cellParams: GridRenderCellParams<string>) {
  return <GridCellExpand value={cellParams.row.comments || ""} width={cellParams.colDef.computedWidth} />;
}

const modelOperators: GridFilterOperator[] = [
  ...getGridStringOperators().slice(0, 1),
  notContainsStringOperator,
  ...getGridStringOperators().slice(1),
];

export const columns = [
  {
    field: "label",
    headerName: "Label",
    width: 125,
    ...linkColDef<IndexedInventory>(({ row }) => {
      if (row.inventoryType === "partner") return null;
      return `/pickups/${row.pickupId}/inventories/${row.id}${window.location.pathname.includes("inventories") ? "?via=inventories" : ""}`;
    }),
  },
  {
    field: "id",
    headerName: "ID",
    type: "number",
    width: 90,
    ...linkColDef<IndexedInventory>(({ row }) => {
      if (row.inventoryType === "partner") return null;
      return `/pickups/${row.pickupId}/inventories/${row.id}${window.location.pathname.includes("inventories") ? "?via=inventories" : ""}`;
    }),
  },
  {
    field: "merchantProductNumber",
    headerName: "Merchant Product Number",
    width: 125,
  },
  { field: "lineId", headerName: "Line ID", width: 125 },
  { field: "warehouseName", ...warehouseColDef },
  {
    field: "pickupId",
    headerName: "Pickup ID",
    width: 125,
    ...linkColDef<IndexedInventory>(({ row }) => `/pickups/${row.pickupId}`),
  },
  {
    field: "printed", // previously printedAt
    type: "boolean",
    headerName: "Printed",
    width: 125,
    sortable: false,
  },
  { field: "inventoryType", headerName: "Type", width: 125, ...singleSelectColDef(["internal", "partner"]) },
  { field: "inventoryImagesCount", headerName: "Image Count", width: 125, type: "number" },
  { field: "iqInventoryId", headerName: "IQ ID", width: 150 },
  { field: "iqPoIdLine", headerName: "IQ PO ID LINE", width: 150 },
  {
    field: "warehouseLocation",
    headerName: "Location",
    description: "Warehouse Location",
    width: 250,
    editable: true,
    ...linkColDef<IndexedInventory>(({ row }) => {
      // Nearest first: the asset's own location, then each location it sits inside.
      const locationId = row.locationAncestorIds?.[0];
      return locationId ? `/locations/${locationId}/edit` : null;
    }),
  },
  {
    // Filter only. The value is a list of location ids, not something to show in the grid.
    field: "locationAncestorIds",
    headerName: "Inside location",
    description: "This location or one nested inside it",
    type: "number",
    sortable: false,
    hide: true,
    hideable: false,
  },
  { field: "manifestId", headerName: "Manifest ID", width: 250 },
  { field: "manufacturer", headerName: "Manufacturer", width: 125 },
  { field: "deviceType", headerName: "Device Type", width: 150, ...singleSelectColDef(deviceTypes) },
  {
    field: "serialNumber",
    headerName: "Serial Number",
    width: 250,
    ...editableColDefWithValidation(inventoryEditValidation, "serialNumber"),
  },
  {
    field: "serialVerificationRequired",
    headerName: "Serial check",
    description: "Blancco wrote a serial that already belongs to another inventory",
    type: "boolean",
    width: 140,
    renderCell: (params: GridRenderCellParams) =>
      params.row.serialVerificationRequired ? <Chip size="small" color="warning" label="Check serial" /> : null,
  },
  {
    field: "model",
    headerName: "Model",
    width: 200,
    editable: true,
    filterOperators: modelOperators,
  },
  { field: "sku", headerName: "SKU", width: 150 },
  { field: "condition", headerName: "Condition", width: 125, ...singleSelectColDef(conditions) },
  { field: "keyboardLanguage", headerName: "Keyboard Language", width: 125 },
  { field: "batteryCount", headerName: "Battery Cycles", width: 125, type: "number" },
  { field: "batteryHealth", headerName: "Battery Health", width: 125 },
  { field: "inventoryGroup", headerName: "Inventory Group", width: 125 },
  { field: "dynamicInventoryGroup", headerName: "Dynamic Inventory Group", width: 125 },
  { field: "state", headerName: "State", width: 150 },
  { field: "invoicedAt", headerName: "Invoiced At", width: 150, ...dateColDef },
  { field: "enrolledStatus", headerName: "Enrolled", width: 150, ...singleSelectColDef(enrolledStatuses) },
  { field: "enrolledOrganization", headerName: "Enrolled Org", width: 175 },
  { field: "drive1ErasureStatus", headerName: "Data Wipe", width: 125 },
  { field: "securityLevel", width: 125 },
  { field: "year", ...yearColDef },
  { field: "quantity", headerName: "Quantity", width: 100, type: "number" },
  { field: "listed", headerName: "Listed As", width: 150 },
  { field: "color", headerName: "Color", width: 150, ...singleSelectColDef(colors) },
  { field: "ram", headerName: "RAM", width: 150 },
  { field: "cpu", headerName: "CPU", width: 250 },
  { field: "gpu", headerName: "GPU", width: 250 },
  { field: "hardDrive", headerName: "Hard Drive", width: 150 },
  { field: "hardDriveSizeInGb", headerName: "Drive Size (GB)", type: "number", width: 150 },
  { field: "comments", headerName: "Comments", width: 300, renderCell: renderCommentsCell, sortable: false },
  // TODO fetch server tag names? Or index all tag values?
  { field: "tagNames", headerName: "Tags", width: 250, sortable: false },
  { field: "assetTag", headerName: "Asset Tag", width: 250, editable: true },
  { field: "createdAt", headerName: "Created At", width: 200, ...dateColDef },
];
